// src/components/ScenView.jsx
// Last updated: 2024-11-19 - Mobile view fix
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Typography,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  useNotification,
  isPermissionDenied,
  getPermissionDeniedText,
  notifyPermissionError,
} from '@1823-partners/core';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';

import DealGrid from './DealGrid';
import MobileDealCards from './MobileDealCards';
import MobileBottomActionBar from './MobileBottomActionBar';
import MobileTabbedPanels from './MobileTabbedPanels';
import DesktopStackedPanels from './DesktopStackedPanels';
import CollapsibleFilterBar from './CollapsibleFilterBar';
import AddSecurityModal from './AddSecurityModal';
import FilterBar from './FilterBar';
import SaveConfirmDialog from './SaveConfirmDialog';
import GenerateCustomPricesDialog from './GenerateCustomPricesDialog';

import {
  fetchConfigs,
  fetchDeals,
  fetchDealCashflow,
  fetchDealAnalytics,
  checkAdcoEnabled,
  fetchDealAdco,
  fetchEquityTranches,
  runDealAdco,
  fetchDealPrice,
  generateCashflowAnalytics,
  generateComparison,
  saveDeal,
  fetchPurchasePrices,
} from '../api';
import { computeSaveChanges, dealToSaveWithoutGenerate } from '../utils/saveChangesUtils';
import {
  applyStaticAdcoOverrides,
  getStaticAdcoOverrides,
  isAdcoEligibleSecurity,
} from '../utils/adcoDisplayUtils';

const splitCondensedValues = (value) => String(value || '')
  .split(',')
  .map(item => item.trim())
  .filter(Boolean);

// Whether two /save requests would write the same thing. Generated rows are only
// replaced wholesale (by a Generate or ADCO run), so they compare by reference;
// an empty list stands for "none" however it was built.
const sameRows = (a, b) => a === b || (Array.isArray(a) && Array.isArray(b) && !a.length && !b.length);
const isSameSaveRequest = (a, b) => (
  ['cashflow', 'analytics', 'parsed', 'adco'].every(key => sameRows(a[key], b[key]))
  && JSON.stringify([a.deal, a.tranches, a.ownPcts, a.purchasePrices])
    === JSON.stringify([b.deal, b.tranches, b.ownPcts, b.purchasePrices])
);

export const mergeAlignedDealTranches = (existingDeal, updatedSecurityData) => {
  const trancheCusips = new Map();
  const addCusip = (trancheValue, cusipValue) => {
    const tranche = String(trancheValue || '').trim();
    const cusip = String(cusipValue || '').trim();
    if (!tranche) return;
    if (!trancheCusips.has(tranche)) trancheCusips.set(tranche, []);
    const cusips = trancheCusips.get(tranche);
    if (cusip && !cusips.includes(cusip)) cusips.push(cusip);
  };
  const addSource = (deal) => {
    const tranches = splitCondensedValues(deal?.Tranche);
    const cusips = splitCondensedValues(deal?.CUSIP);
    const explicitMap = new Map();
    const rawTrancheMap = deal?.tranche_map;
    if (rawTrancheMap && typeof rawTrancheMap === 'object' && !Array.isArray(rawTrancheMap)) {
      Object.entries(rawTrancheMap).forEach(([trancheValue, cusipValues]) => {
        const tranche = String(trancheValue || '').trim();
        if (!tranche) return;
        const normalizedCusips = (Array.isArray(cusipValues) ? cusipValues : [cusipValues])
          .map(cusip => String(cusip || '').trim())
          .filter(Boolean);
        if (!normalizedCusips.length) return;
        if (!explicitMap.has(tranche)) explicitMap.set(tranche, []);
        normalizedCusips.forEach((cusip) => {
          if (!explicitMap.get(tranche).includes(cusip)) explicitMap.get(tranche).push(cusip);
        });
      });
    }

    const orderedTranches = [...new Set([...tranches, ...explicitMap.keys()])];
    orderedTranches.forEach((tranche) => {
      const explicitCusips = explicitMap.get(tranche);
      if (explicitCusips) {
        explicitCusips.forEach(cusip => addCusip(tranche, cusip));
        return;
      }
      tranches.forEach((sourceTranche, index) => {
        if (sourceTranche !== tranche) return;
        addCusip(tranche, cusips[index] || (cusips.length === 1 ? cusips[0] : ''));
      });
    });
  };

  addSource(existingDeal);
  addSource(updatedSecurityData);

  const normalizedTrancheMap = Object.fromEntries(
    [...trancheCusips.entries()].filter(([, cusips]) => cusips.length > 0)
  );
  const alignedPairs = Object.entries(normalizedTrancheMap).flatMap(([tranche, cusips]) => (
    cusips.map(cusip => ({ tranche, cusip }))
  ));

  return {
    'Tranche': alignedPairs.map(({ tranche }) => tranche).join(', '),
    'CUSIP': alignedPairs.map(({ cusip }) => cusip).join(', '),
    tranche_map: normalizedTrancheMap,
  };
};

export default function ScenView({ userId }) {
  const theme = useTheme();
  const { showNotification } = useNotification();
  // useMediaQuery already re-renders on breakpoint changes — a raw resize
  // listener here was re-rendering the whole view on every resize pixel.
  const isMobile = useMediaQuery('(max-width: 1200px)');

  // --- STATE ------------------------------------------------------------
  const [columns, setColumns] = useState([]);
  const [types, setTypes] = useState([]);
  const [owners, setOwners] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [deals, setDeals] = useState([]);

  const [modifiedDate, setModifiedDate] = useState(new Date());
  const [selTypes, setSelTypes] = useState([]);
  const [selOwners, setSelOwners] = useState([]);
  const [adcoOnly, setAdcoOnly] = useState(false);

  const [selectedRows, setSelectedRows] = useState([]);
  const [availableTranches, setAvailableTranches] = useState([]);
  const [selectedTranches, setSelectedTranches] = useState([]);

  const [panelAnalyticsTranches, setPanelAnalyticsTranches] = useState([]);
  const [panelCashflowTranche, setPanelCashflowTranche] = useState('');
  const [panelAdcoTranche, setPanelAdcoTranche] = useState('');

  const [analyticsData, setAnalyticsData] = useState([]);
  const [cashflowData, setCashflowData] = useState([]);
  // One-row-per-period cashflow rows (DATE-keyed). Populated either by a
  // generate/comparison response's cashflowMonthly key, or by refetching
  // /deal/cashflow?grain=month on the stored-data path.
  const [cashflowMonthlyData, setCashflowMonthlyData] = useState([]);
  const [cashflowGrain, setCashflowGrain] = useState('year');
  const [parsedData, setParsedData] = useState([]);
  const [analyticsMainData, setAnalyticsMainData] = useState([]);
  const [cashflowMainData, setCashflowMainData] = useState([]);
  const [adcoData, setAdcoData] = useState([]);
  const [staticAdcoOverrides, setStaticAdcoOverrides] = useState({});
  const effectiveAdcoData = useMemo(
    () => applyStaticAdcoOverrides(adcoData, staticAdcoOverrides) || [],
    [adcoData, staticAdcoOverrides]
  );
  const [adcoEnabled, setAdcoEnabled] = useState({});
  const [equityTranches, setEquityTranches] = useState({});

  const [addSecurityOpen, setAddSecurityOpen] = useState(false);
  const [editSecurityData, setEditSecurityData] = useState(null);

  // Price calculator state - 3 mutually exclusive rows
  const [priceTrancheTargets, setPriceTrancheTargets] = useState({});
  const [yieldTrancheTargets, setYieldTrancheTargets] = useState({});
  const [ownPctTrancheTargets, setOwnPctTrancheTargets] = useState({});
  
  // Generation error state for displaying failure messages
  const [generationError, setGenerationError] = useState('');
  const [spreadTrancheTargets, setSpreadTrancheTargets] = useState({});
  const [priceData, setPriceData] = useState([]);

  const [dealNameFilter, setDealNameFilter] = useState('');
  const [useAsofdt, setUseAsofdt] = useState(false);
  const [asofdt, setAsofdt] = useState(new Date());
  const [calculateDisabled, setCalculateDisabled] = useState(true);

  const [loading, setLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [cashflowLoading, setCashflowLoading] = useState(false);
  const [adcoLoading, setAdcoLoading] = useState(false);
  const lastAdcoFetchRef = useRef(null); // Track last ADCO fetch params to prevent duplicates
  // Pristine deal as loaded from Snowflake, kept so Save can diff every changed
  // field (not just price/ownership). Preserved across edits of the same deal;
  // reset when a different deal is selected/cleared. Shape: { name, data }.
  const originalDealRef = useRef(null);
  // The /save request the open confirmation dialog describes (see buildSaveRequest).
  const saveRequestRef = useRef(null);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasGeneratedData, setHasGeneratedData] = useState(false);
  const [generatedWithYieldOrSpread, setGeneratedWithYieldOrSpread] = useState(false);
  const [saveChanges, setSaveChanges] = useState([]);
  const [adcoWasRun, setAdcoWasRun] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [generateDialogAnchorEl, setGenerateDialogAnchorEl] = useState(null);

  // Comparison state
  const [shockBps, setShockBps] = useState('');
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  // Mobile-specific state
  const [mobileTabValue, setMobileTabValue] = useState(0);



  // --- EFFECTS ----------------------------------------------------------

  // 1) Load configuration
  useEffect(() => {
    fetchConfigs()
      .then(r => {
        const c = r.data;
        setColumns(c.columns || []);
        setTypes(c.types || []);
        setOwners(c.owners || []);
      })
      .catch(error => {
        notifyPermissionError(showNotification, error);
        // fallback
        setColumns([{ field: 'Deal Name', headerName: 'Deal Name' }]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) Fetch all deals when modifiedDate changes
  const loadDeals = useCallback(() => {
    setLoading(true);
    fetchDeals({
      modifiedDate: dayjs(modifiedDate).format('YYYY-MM-DD'),
      types: [],
      owners: [],
    })
      .then(r => {
        setAllDeals(r.data);
      })
      .catch(error => {
        notifyPermissionError(showNotification, error);
        setAllDeals([]);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedDate]);
  useEffect(() => { loadDeals(); }, [loadDeals]);

  // 3) Filter the regular Trading grid locally.
  useEffect(() => {
    let filtered = allDeals;

    // Filter by asset type
    if (selTypes.length > 0) {
      filtered = filtered.filter(deal => {
        const dealType = deal.Type || deal['Asset Type'] || '';
        return selTypes.includes(dealType);
      });
    }

    // Filter by security owner
    if (selOwners.length > 0) {
      filtered = filtered.filter(deal => {
        const dealOwner = deal['Security Owner'] || deal.Owner || '';
        return selOwners.includes(dealOwner);
      });
    }

    const normalizedDealName = dealNameFilter.trim().toLowerCase();
    if (normalizedDealName) {
      filtered = filtered.filter(deal => String(
        deal['Deal Name'] || deal.DEAL_NAME || ''
      ).toLowerCase().includes(normalizedDealName));
    }

    if (adcoOnly) {
      filtered = filtered.filter(isAdcoEligibleSecurity);
    }

    setDeals(filtered);
  }, [allDeals, selTypes, selOwners, dealNameFilter, adcoOnly]);

  // 3) Update tranche lists & ADCO visibility on row selection or data generation
  useEffect(() => {
    let tranches = [];
    let cusips = [];

    // Only try to extract from generated data if it actually has tranches
    // Priority 1: Use generated analytics data if available and has tranches
    if (Array.isArray(analyticsData) && analyticsData.length > 0) {
      const trancheSet = new Set();
      analyticsData.forEach(row => {
        if (row?.Tranche) trancheSet.add(row.Tranche);
      });
      const extractedTranches = Array.from(trancheSet);

      // Only use if we actually found tranches in the data
      if (extractedTranches.length > 0) {
        tranches = extractedTranches;
        // Extract CUSIPs from analytics data
        const cusipSet = new Set();
        analyticsData.forEach(row => {
          if (row?.CUSIP) cusipSet.add(row.CUSIP);
        });
        cusips = Array.from(cusipSet);
      }
    }
    // Priority 2: Use generated cashflow data if no analytics but cashflow exists
    else if (Array.isArray(cashflowData) && cashflowData.length > 0) {
      // For now, let's skip cashflow-based tranche extraction if analytics exists
      // and just use the available tranches that were already set
      if (availableTranches.length > 0) {
        tranches = [...availableTranches];
      } else {
        // Fallback: try to extract from cashflow data structure
        const trancheSet = new Set();
        const cusipSet = new Set();

        // Look for tranche information in the cashflow data
        cashflowData.forEach((row, index) => {
          // Look for tranche-related fields
          Object.keys(row || {}).forEach(key => {
            if (key.toLowerCase().includes('tranche') && row[key]) {
              trancheSet.add(row[key]);
            }
            if (key.toLowerCase().includes('cusip') && row[key]) {
              cusipSet.add(row[key]);
            }
          });
        });

        const extractedTranches = Array.from(trancheSet);
        const extractedCusips = Array.from(cusipSet);

        // Only use if we actually found tranches
        if (extractedTranches.length > 0) {
          tranches = extractedTranches;
          cusips = extractedCusips;
        }
      }
    }
    // Priority 3: Fall back to selected rows from API
    else if (selectedRows?.length > 0) {
      const tr = new Set();
      selectedRows.forEach(r => {
        (r.Tranche || '').split(',').forEach(t => tr.add(t.trim()));
        (r.CUSIP || '').split(',').forEach(cu => cusips.push(cu.trim()));
      });
      tranches = Array.from(tr);
    }

    // Don't clear existing tranches if we don't find any in generated data
    if (tranches.length === 0 && availableTranches.length > 0) {
      tranches = [...availableTranches];
    }
    setAvailableTranches(tranches);
    setSelectedTranches(tranches);
    setPanelAnalyticsTranches(tranches);
    setPanelCashflowTranche(tranches[0] || '');
    setPanelAdcoTranche(tranches[0] || '');
    setCalculateDisabled(selectedRows.length === 0);
  }, [selectedRows]);

  // 3b) Update tranche lists when generated data arrives (only for freshly generated data, not fetched)
  useEffect(() => {
    // Only update tranches from analytics if we have generated data AND don't already have available tranches
    // This prevents overwriting deal-sourced tranches with potentially incomplete analytics response
    if (!Array.isArray(analyticsData) || analyticsData.length === 0) return;
    
    // If we already have available tranches from the deal, don't overwrite them
    // The deal's Tranche column is the source of truth for available tranches
    if (availableTranches.length > 0) {
      return;
    }

    const trancheSet = new Set();

    // Analytics data has tranche-prefixed columns like "Tranche D: 03331PAG3"
    if (analyticsData.length > 0) {
      const firstRow = analyticsData[0];
      Object.keys(firstRow).forEach(key => {
        // Look for tranche-prefixed columns
        const trancheMatch = key.match(/^Tranche\s+([A-Z0-9]+):/i);
        if (trancheMatch) {
          trancheSet.add(trancheMatch[1]); // Extract tranche name (D, E, F, SUBORD, etc.)
        }
      });
    }

    const tranches = Array.from(trancheSet);

    // Only update if we found tranches and we don't already have tranches set
    if (tranches.length > 0) {
      setAvailableTranches(tranches);
      setSelectedTranches(tranches);
      setPanelAnalyticsTranches(tranches);
      setPanelCashflowTranche(tranches[0] || '');
      setPanelAdcoTranche(tranches[0] || '');
    }
  }, [analyticsData, availableTranches.length]);

  // Update panel tranche selectors when main tranche selection changes
  useEffect(() => {
    if (selectedTranches.length > 0) {
      // Set analytics panel to all selected tranches
      setPanelAnalyticsTranches(selectedTranches);

      // Set cashflow panel to first selected tranche
      setPanelCashflowTranche(selectedTranches[0] || '');

      // Set ADCO panel to first selected tranche
      setPanelAdcoTranche(selectedTranches[0] || '');
    } else {
      // Clear all panel selections if no tranches selected
      setPanelAnalyticsTranches([]);
      setPanelCashflowTranche('');
      setPanelAdcoTranche('');
    }
  }, [selectedTranches]);

  // 4a) Auto-fetch analytics (only if no generated data exists)
  useEffect(() => {
    if (!selectedRows.length || !panelAnalyticsTranches.length) {
      return;
    }

    // Don't fetch if we have generated data
    if (hasGeneratedData) {
      return;
    }

    // Don't fetch if we already have analytics data
    if (Array.isArray(analyticsData) && analyticsData.length > 0) {
      return;
    }

    const dealData = selectedRows[0];
    const deal = dealData['Deal Name'];
    const md = dayjs(modifiedDate).format('YYYY-MM-DD');

    // Extract and format asofdt from deal if it exists
    let asofdt = null;
    if (dealData['ASOFDT']) {
      asofdt = dayjs(dealData['ASOFDT']).format('YYYY-MM-DD');
    }

    setAnalyticsLoading(true);
    fetchDealAnalytics(deal, panelAnalyticsTranches, md, asofdt)
      .then(r => {
        setAnalyticsData(r.data);
      })
      .catch(error => {
        notifyPermissionError(showNotification, error);
        setAnalyticsData([]);
      })
      .finally(() => setAnalyticsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, panelAnalyticsTranches, modifiedDate]);

  // 4b) Auto-fetch cashflow (only if no generated data exists)
  useEffect(() => {
    if (!selectedRows.length) {
      return;
    }

    // Don't fetch if we have generated data — the generate/comparison
    // response already carries both grains (cashflow + cashflowMonthly), so
    // the grain toggle switches instantly with no refetch in that case.
    if (hasGeneratedData) {
      return;
    }

    // Don't fetch if we already have cashflow data for the current grain
    const haveData = cashflowGrain === 'month'
      ? Array.isArray(cashflowMonthlyData) && cashflowMonthlyData.length > 0
      : Array.isArray(cashflowData) && cashflowData.length > 0;
    if (haveData) {
      return;
    }

    const deal = selectedRows[0]['Deal Name'];
    const md = dayjs(modifiedDate).format('YYYY-MM-DD');

    setCashflowLoading(true);
    // Fetch cashflow for selected tranches, at the current grain
    fetchDealCashflow(deal, selectedTranches, md, cashflowGrain)
      .then(r => {
        if (cashflowGrain === 'month') {
          setCashflowMonthlyData(r.data);
        } else {
          setCashflowData(r.data);
        }

        // Set default tranche if not already set
        if (!panelCashflowTranche && availableTranches.length > 0) {
          setPanelCashflowTranche(availableTranches[0]);
        }
        if (!panelAdcoTranche && availableTranches.length > 0) {
          setPanelAdcoTranche(availableTranches[0]);
        }
      })
      .catch(error => {
        notifyPermissionError(showNotification, error);
        if (cashflowGrain === 'month') {
          setCashflowMonthlyData([]);
        } else {
          setCashflowData([]);
        }
      })
      .finally(() => setCashflowLoading(false));
    // panelCashflowTranche is intentionally NOT a dependency: it's only read
    // to set a default after the fetch — including it caused a redundant
    // refetch every time the user switched the cashflow tranche dropdown.
    // hasGeneratedData/cashflowData/cashflowMonthlyData are read but not
    // listed either, for the same reason: they're checked at fetch time, not
    // meant to retrigger this effect (cashflowGrain is the intended trigger
    // for a refetch on toggle).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, modifiedDate, selectedTranches, cashflowGrain]);

  // 4c) Auto-check ADCO enabled and fetch ADCO data (debounced to prevent multiple calls)
  useEffect(() => {
    let cancelled = false;
    
    if (!selectedRows.length || !selectedTranches.length) {
      setAdcoEnabled({});
      setAdcoData([]);
      setAdcoLoading(false);
      lastAdcoFetchRef.current = null;
      return;
    }

    const deal = selectedRows[0]['Deal Name'];
    const assetType = selectedRows[0]['Type'] || '';
    const intexDeal = selectedRows[0]['Intex Deal'] || selectedRows[0]['INTEX_DEAL'] || selectedRows[0]['intexDeal'] || null;
    const md = dayjs(modifiedDate).format('YYYY-MM-DD');
    const trancheKey = [...selectedTranches].sort().join(',');
    
    // Create a unique key for this fetch request
    const fetchKey = `${deal}|${assetType}|${trancheKey}|${md}`;
    
    // Skip if we already fetched with these exact parameters
    if (lastAdcoFetchRef.current === fetchKey) {
      return;
    }

    // Debounce the ADCO fetch to prevent multiple rapid calls
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      
      // Double-check the key hasn't been set by another effect
      if (lastAdcoFetchRef.current === fetchKey) {
        return;
      }
      
      lastAdcoFetchRef.current = fetchKey;
      setAdcoLoading(true);
      
      checkAdcoEnabled(deal, assetType, selectedTranches, md, intexDeal)
        .then(r => {
          if (cancelled) return;
          setAdcoEnabled(r.data || {});

          // If any tranche is enabled, fetch ADCO data
          const hasEnabledTranches = Object.values(r.data || {}).some(enabled => enabled);
          if (hasEnabledTranches) {
            return fetchDealAdco(deal, assetType, selectedTranches, md)
              .then(adcoResponse => {
                if (cancelled) return;
                setAdcoData(adcoResponse.data || []);
              })
              .catch(error => {
                if (cancelled) return;
                setAdcoData([]);
              });
          } else {
            setAdcoData([]);
          }
        })
        .catch(error => {
          if (cancelled) return;
          notifyPermissionError(showNotification, error);
          setAdcoEnabled({});
          setAdcoData([]);
        })
        .finally(() => {
          if (!cancelled) setAdcoLoading(false);
        });

      fetchEquityTranches(deal, selectedTranches, md)
        .then(r => { if (!cancelled) setEquityTranches(r.data || {}); })
        .catch(() => { if (!cancelled) setEquityTranches({}); });
    }, 300); // 300ms debounce
    
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [selectedRows, selectedTranches, modifiedDate]);

  // --- HANDLERS ---------------------------------------------------------

  const loadDeal = useCallback((row) => {
    // Extract tranches directly from the deal's Tranche column
    const dealTranches = (row.Tranche || '').split(',').map(t => t.trim()).filter(t => t);

    // Clear ADCO fetch ref to allow refetching for this deal
    lastAdcoFetchRef.current = null;

    setSelectedRows([row]);
    setStaticAdcoOverrides(getStaticAdcoOverrides(row));
    setAvailableTranches(dealTranches);
    setSelectedTranches(dealTranches);
    setPanelAnalyticsTranches(dealTranches);
    setPanelCashflowTranche(dealTranches[0] || '');
    setPanelAdcoTranche(dealTranches[0] || '');
    setHasUnsavedChanges(false);
    // Clear generated data when loading deal
    setAnalyticsData([]);
    setCashflowData([]);
    setCashflowMonthlyData([]);
    setCashflowGrain('year');
    setParsedData([]);
    setAnalyticsMainData([]);
    setCashflowMainData([]);
    setAdcoData([]);
    setAdcoEnabled({});
    setHasGeneratedData(false);
    setAdcoWasRun(false);

    // Default the pricing popup's Price field to the most recent blotter buy
    // price per tranche (the PURCHASE_PRICE default). The user can override it
    // in the popup; whatever is there at save time is persisted as PURCHASE_PRICE.
    setPriceTrancheTargets({});
    if (row['Deal Name'] && dealTranches.length) {
      fetchPurchasePrices(row['Deal Name'], dealTranches, dayjs(modifiedDate).format('YYYY-MM-DD'))
        .then(r => {
          if (r?.data && Object.keys(r.data).length) {
            setPriceTrancheTargets(r.data);
          }
        })
        .catch(() => {});
    }
  }, [modifiedDate]);

  // Stable refs so the memo'd DealGrid doesn't rebuild its colDefs and
  // re-render on every unrelated ScenView render (e.g. typing in the
  // Deal Name filter).
  const handleEditDeal = useCallback((rowData) => {
    // TEMP edit-debug: what row is the Edit action handed on (re-)open?
    console.log('[edit-debug] handleEditDeal row =', JSON.parse(JSON.stringify(rowData ?? null)));
    // Capture the pristine baseline the first time a given deal is opened for
    // editing (e.g. via the grid row action without selecting it first), and
    // preserve it across repeated edits of the same deal so the Save diff stays
    // against the as-loaded Snowflake values.
    if (originalDealRef.current?.name !== rowData?.['Deal Name']) {
      originalDealRef.current = { name: rowData?.['Deal Name'], data: { ...rowData } };
    }
    // Open the AddSecurityModal in edit mode with the row data
    setEditSecurityData(rowData);
    setAddSecurityOpen(true);
  }, []);

  const handleCloneDeal = useCallback((rowData) => {
    // Open the AddSecurityModal with the row data but clear deal name and tranches
    const clonedData = { ...rowData };
    delete clonedData['Deal Name'];
    delete clonedData['DEAL_NAME'];
    delete clonedData['Tranche'];
    delete clonedData['TRANCHE'];
    delete clonedData['CUSIP'];
    setEditSecurityData(clonedData);
    setAddSecurityOpen(true);
  }, []);

  const handleGridSelectionChanged = useCallback((rows) => {
    setSelectedRows(rows);
    setStaticAdcoOverrides(
      rows.length > 0 ? getStaticAdcoOverrides(rows[0]) : {}
    );
    if (rows.length > 0) {
      // Snapshot the pristine (as-loaded) deal so Save can show every field
      // changed this session, not just the price-popup fields.
      originalDealRef.current = { name: rows[0]['Deal Name'], data: { ...rows[0] } };
      setHasUnsavedChanges(false);
      // Clear generated data when selecting different deal
      setAnalyticsData([]);
      setCashflowData([]);
      setCashflowMonthlyData([]);
      setCashflowGrain('year');
      setParsedData([]);
      setAnalyticsMainData([]);
      setAdcoData([]);
      setAdcoEnabled({});
      setHasGeneratedData(false);

    } else {
      originalDealRef.current = null;
    }
  }, []);


  // Generate both cashflow and analytics in one call
  const handleGenerateCashflowAnalytics = async () => {
    if (!selectedRows.length) {
      console.warn('No deals selected for generation');
      return;
    }

    // Use selected tranches for generation
    if (!selectedTranches.length) {
      console.warn('No tranches selected for generation');
      return;
    }

    // Route to comparison handler if shock bps is set
    if (shockBps && Number(shockBps) > 0) {
      return handleGenerateComparison();
    }

    // Build per-tranche targets - each tranche can have price, yield, or spread
    // Send all directly to generate API which will handle price calculation
    const tranche_target = {};
    
    selectedTranches.forEach(tranche => {
      const entry = {};
      // Check what's entered for each tranche (priority: price > yield > spread)
      if (priceTrancheTargets[tranche] !== undefined && priceTrancheTargets[tranche] !== '' && priceTrancheTargets[tranche] !== null) {
        entry.price = priceTrancheTargets[tranche];
      } else if (yieldTrancheTargets[tranche] !== undefined && yieldTrancheTargets[tranche] !== '' && yieldTrancheTargets[tranche] !== null) {
        entry.yield = yieldTrancheTargets[tranche];
      } else if (spreadTrancheTargets[tranche] !== undefined && spreadTrancheTargets[tranche] !== '' && spreadTrancheTargets[tranche] !== null) {
        entry.spread = spreadTrancheTargets[tranche];
      }
      if (ownPctTrancheTargets[tranche] !== undefined && ownPctTrancheTargets[tranche] !== '' && ownPctTrancheTargets[tranche] !== null) {
        entry.own_pct = ownPctTrancheTargets[tranche];
      }
      tranche_target[tranche] = entry;
    });

    // Saving is blocked only for custom yield/spread (analytics computed off an
    // ad-hoc curve). A custom price is allowed and is persisted as PURCHASE_PRICE.
    const hasYieldOrSpread = Object.values(tranche_target).some(
      val => val && (val.yield !== undefined || val.spread !== undefined)
    );

    const dealData = selectedRows[0];

    // modifiedDate is always modifiedDate
    const md = dayjs(modifiedDate).format('YYYY-MM-DD');

    // Prepare complete deal data with ALL columns (including empty ones)
    const completeDealData = {};

    // First, add all column definitions with empty/null values
    columns.forEach(col => {
      const fieldName = col.headerName || col.field;
      if (dealData.hasOwnProperty(fieldName)) {
        completeDealData[fieldName] = dealData[fieldName];
      } else if (dealData.hasOwnProperty(col.field)) {
        completeDealData[fieldName] = dealData[col.field];
      } else {
        // Add empty value for missing columns
        completeDealData[fieldName] = null;
      }
    });

    // Then overlay any values from dealData that might not be in column definitions
    Object.keys(dealData).forEach(key => {
      completeDealData[key] = dealData[key];
    });

    // Determine which asofdt to send as parameter
    let asofdtParam = null;
    if (useAsofdt) {
      // Use custom ASOFDT if enabled
      asofdtParam = dayjs(asofdt).format('YYYY-MM-DD');
      completeDealData['ASOFDT'] = asofdtParam;
    } else if (dealData['ASOFDT']) {
      // Otherwise use ASOFDT from deal data if it exists
      asofdtParam = dayjs(dealData['ASOFDT']).format('YYYY-MM-DD');
    }

    // Set loading states
    setGenerationLoading(true);
    setAnalyticsLoading(true);
    setCashflowLoading(true);
    setGenerationError(''); // Clear any previous error

    // Backend's expand_condensed_deals zips comma-joined CUSIP/Tranche by index,
    // so a multi-tranche deal that ships a single PRE_CUSIP loses every tranche
    // past the first. Reconstruct the CUSIP list from adcoData (which has
    // tranche-level rows) so the lists line up.
    const trancheList = String(completeDealData.Tranche || '')
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    if (trancheList.length > 1) {
      const trancheCusipMap = {};
      (adcoData || []).forEach(r => {
        const tr = r?.TRANCHE || r?.Tranche;
        const cu = r?.CUSIP;
        if (tr && cu && !trancheCusipMap[tr]) trancheCusipMap[tr] = cu;
      });
      const existingCusips = String(completeDealData.CUSIP || '')
        .split(',')
        .map(c => c.trim())
        .filter(Boolean);
      const fallbackCusip = existingCusips[0] || completeDealData.PRE_CUSIP || '';
      const alignedCusips = trancheList.map(
        (t, i) => trancheCusipMap[t] || existingCusips[i] || fallbackCusip
      );
      if (alignedCusips.every(Boolean)) {
        completeDealData.CUSIP = alignedCusips.join(',');
      }
    }

    try {
      // Send tranche_target with price/yield/spread info directly to generate API
      const adcoForGeneration = effectiveAdcoData.length > 0 ? effectiveAdcoData : null;
      const response = await generateCashflowAnalytics(
        completeDealData,
        tranche_target,
        md,
        adcoForGeneration,
        asofdtParam
      );
      // Parse the JSON string response
      const parsedResponse = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

      // Extract cashflow, analytics, analytics_main, parsed data, and prices from response
      const { cashflow, cashflowMonthly, analytics, analytics_main, cashflow_main, parsed, prices, failures } = parsedResponse;
      
      // Helper function to check if data is empty (handles both arrays and objects)
      const isEmpty = (data) => {
        if (!data) return true;
        if (Array.isArray(data)) return data.length === 0;
        if (typeof data === 'object') return Object.keys(data).length === 0;
        return false;
      };

      // Check if response is empty or has no meaningful data
      const isEmptyResponse = !parsedResponse || 
        (isEmpty(cashflow) && 
         isEmpty(analytics) && 
         isEmpty(analytics_main) &&
         isEmpty(cashflow_main) &&
         isEmpty(parsed));

      if (isEmptyResponse) {
        // Treat empty response as failure
        const errorMsg = 'Generation returned no data. The deal may not have valid cashflow/analytics information, or the API response was empty.';
        setGenerationError(errorMsg);
        setAnalyticsData([]);
        setCashflowData([]);
        setCashflowMonthlyData([]);
        setParsedData([]);
        setAnalyticsMainData([]);
        setCashflowMainData([]);
        setPriceData([]);
        // Set hasGeneratedData to true to prevent auto-fetch from re-running
        // (we attempted generation, even if it returned empty)
        setHasGeneratedData(true);
        showNotification('Generation failed: No data returned', 'error');
        return;
      }
      
      // Clear any previous error since we have data
      setGenerationError('');
      
      // Handle prices returned from API (for yield/spread calculations)
      if (prices) {
        // Convert prices object {tranche: price} to array format for display
        const pricesArray = Object.entries(prices).map(([tranche, price]) => ({
          tranche,
          price
        }));
        setPriceData(pricesArray);
      } else {
        setPriceData([]);
      }

      if (analytics) {
        setAnalyticsData(analytics);
      }

      if (cashflow) {
        setCashflowData(cashflow);
      }

      // cashflowMonthly is additive alongside cashflow: same data, one row
      // per period, so the grain toggle can switch instantly with no refetch.
      setCashflowMonthlyData(cashflowMonthly || []);

      if (parsed) {
        setParsedData(parsed);
      }

      // Store analytics_main to be sent as analytics in save API
      if (analytics_main) {
        setAnalyticsMainData(analytics_main);
      }

      // Store cashflow_main to be sent as cashflow in save API
      if (cashflow_main) {
        setCashflowMainData(cashflow_main);
      }

      // Mark as having unsaved changes and generated data
      setHasUnsavedChanges(true);
      setHasGeneratedData(true);
      // Block saving only when a custom yield/spread was used (see handleSave).
      setGeneratedWithYieldOrSpread(hasYieldOrSpread);

      if (Array.isArray(failures) && failures.length > 0) {
        const failedSummary = failures
          .map(f => `${f.tranche || f.cusip}: ${f.error}`)
          .join('; ');
        const failedMsg = `Generation skipped ${failures.length} tranche(s) — ${failedSummary}`;
        setGenerationError(failedMsg);
        showNotification(failedMsg, 'warning');
      } else {
        showNotification('Analytics generated successfully!', 'success');
      }

    } catch (error) {
      setAnalyticsData([]);
      setCashflowData([]);
      setCashflowMonthlyData([]);
      setParsedData([]);
      setAnalyticsMainData([]);
      setCashflowMainData([]);
      setPriceData([]);
      setHasGeneratedData(false);
      // notifyPermissionError already raised a toast carrying the in-app "Request access" button.
      // The inline box repeats the reason for someone who dismissed the toast, so it uses the TEXT
      // variant: the message variant appends a link to the Permissions app, and inside a read-only
      // TextField that URL is not clickable -- it just makes the box look like the only way out
      // while the toast's button is the real one.
      if (notifyPermissionError(showNotification, error)) {
        if (isPermissionDenied(error)) {
          setGenerationError(getPermissionDeniedText(error));
        }
      } else {
        const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error occurred during generation';
        setGenerationError(`Generation failed: ${errorMsg}`);
        showNotification('Generation failed. Please try again.', 'error');
      }
    } finally {
      setGenerationLoading(false);
      setAnalyticsLoading(false);
      setCashflowLoading(false);
    }
  };

  // Generate shock comparison using the entire current form state
  const handleGenerateComparison = async () => {
    if (!selectedRows.length || !selectedTranches.length) return;

    const tranche_target = {};
    selectedTranches.forEach(tranche => {
      const entry = {};
      if (priceTrancheTargets[tranche] !== undefined && priceTrancheTargets[tranche] !== '' && priceTrancheTargets[tranche] !== null) {
        entry.price = priceTrancheTargets[tranche];
      } else if (yieldTrancheTargets[tranche] !== undefined && yieldTrancheTargets[tranche] !== '' && yieldTrancheTargets[tranche] !== null) {
        entry.yield = yieldTrancheTargets[tranche];
      } else if (spreadTrancheTargets[tranche] !== undefined && spreadTrancheTargets[tranche] !== '' && spreadTrancheTargets[tranche] !== null) {
        entry.spread = spreadTrancheTargets[tranche];
      }
      if (ownPctTrancheTargets[tranche] !== undefined && ownPctTrancheTargets[tranche] !== '' && ownPctTrancheTargets[tranche] !== null) {
        entry.own_pct = ownPctTrancheTargets[tranche];
      }
      tranche_target[tranche] = entry;
    });

    const dealData = selectedRows[0];
    const md = dayjs(modifiedDate).format('YYYY-MM-DD');

    const completeDealData = {};
    columns.forEach(col => {
      const fieldName = col.headerName || col.field;
      if (dealData.hasOwnProperty(fieldName)) {
        completeDealData[fieldName] = dealData[fieldName];
      } else if (dealData.hasOwnProperty(col.field)) {
        completeDealData[fieldName] = dealData[col.field];
      } else {
        completeDealData[fieldName] = null;
      }
    });
    Object.keys(dealData).forEach(key => {
      completeDealData[key] = dealData[key];
    });

    let asofdtParam = null;
    if (useAsofdt) {
      asofdtParam = dayjs(asofdt).format('YYYY-MM-DD');
      completeDealData['ASOFDT'] = asofdtParam;
    } else if (dealData['ASOFDT']) {
      asofdtParam = dayjs(dealData['ASOFDT']).format('YYYY-MM-DD');
    }

    // Same fix-up as handleGenerateCashflowAnalytics: align comma-joined CUSIP
    // with comma-joined Tranche so the backend's positional zip in
    // expand_condensed_deals doesn't drop tranches past the first.
    const trancheList = String(completeDealData.Tranche || '')
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    if (trancheList.length > 1) {
      const trancheCusipMap = {};
      (adcoData || []).forEach(r => {
        const tr = r?.TRANCHE || r?.Tranche;
        const cu = r?.CUSIP;
        if (tr && cu && !trancheCusipMap[tr]) trancheCusipMap[tr] = cu;
      });
      const existingCusips = String(completeDealData.CUSIP || '')
        .split(',')
        .map(c => c.trim())
        .filter(Boolean);
      const fallbackCusip = existingCusips[0] || completeDealData.PRE_CUSIP || '';
      const alignedCusips = trancheList.map(
        (t, i) => trancheCusipMap[t] || existingCusips[i] || fallbackCusip
      );
      if (alignedCusips.every(Boolean)) {
        completeDealData.CUSIP = alignedCusips.join(',');
      }
    }

    const comparisonConfig = {
      comparison_mode: 'shock',
      shock_bps: Number(shockBps),
    };

    setComparisonLoading(true);
    setGenerationError('');

    try {
      const adcoForGeneration = effectiveAdcoData.length > 0 ? effectiveAdcoData : null;

      const response = await generateComparison(
        completeDealData,
        tranche_target,
        md,
        comparisonConfig,
        adcoForGeneration,
        asofdtParam
      );

      const result = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      setComparisonData(result);
      setShowComparison(true);
      showNotification('Comparison generated successfully!', 'success');
    } catch (error) {
      // notifyPermissionError already raised a toast carrying the in-app "Request access" button.
      // The inline box repeats the reason for someone who dismissed the toast, so it uses the TEXT
      // variant: the message variant appends a link to the Permissions app, and inside a read-only
      // TextField that URL is not clickable -- it just makes the box look like the only way out
      // while the toast's button is the real one.
      if (notifyPermissionError(showNotification, error)) {
        if (isPermissionDenied(error)) {
          setGenerationError(getPermissionDeniedText(error));
        }
      } else {
        const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
        setGenerationError(`Comparison failed: ${errorMsg}`);
        showNotification('Comparison failed. Please try again.', 'error');
      }
    } finally {
      setComparisonLoading(false);
    }
  };

  // Clear loaded deal
  const handleClearDeal = () => {
    originalDealRef.current = null;
    setSelectedRows([]);
    setStaticAdcoOverrides({});
    setAvailableTranches([]);
    setSelectedTranches([]);
    setPanelAnalyticsTranches([]);
    setPanelCashflowTranche('');
    setPanelAdcoTranche('');
    setAnalyticsData([]);
    setCashflowData([]);
    setCashflowMonthlyData([]);
    setCashflowGrain('year');
    setParsedData([]);
    setAnalyticsMainData([]);
    setCashflowMainData([]);
    setAdcoData([]);
    setAdcoEnabled({});
    setPriceData([]);
    setPriceTrancheTargets({});
    setYieldTrancheTargets({});
    setSpreadTrancheTargets({});
    setHasUnsavedChanges(false);
    setHasGeneratedData(false);
    setGenerationError('');
    setGeneratedWithYieldOrSpread(false);
    setAdcoWasRun(false);
    setShockBps('');
    setComparisonData(null);
    setShowComparison(false);
  };

  // Every change a save would write, diffed against the deal as it was loaded
  // from Snowflake: deal-level assumption edits (CPR, CDR, etc.) plus
  // per-tranche ownership % and purchase price from the pricing popup.
  const pendingSaveChanges = useMemo(() => computeSaveChanges({
    original: originalDealRef.current?.data || {},
    current: selectedRows[0] || {},
    selectedTranches,
    ownPctTrancheTargets,
    priceTrancheTargets,
  }), [selectedRows, selectedTranches, ownPctTrancheTargets, priceTrancheTargets]);
  const hasGeneratedSaveData = cashflowMainData.length > 0 && analyticsMainData.length > 0 && parsedData.length > 0;
  // Edits that never reach the cashflow engine (Security Owner) save without a
  // Generate, as the loaded row with only those fields swapped in, under its stored
  // tranche/CUSIP pairs; a re-run ADCO changes the cashflows, so it still needs one.
  const dealWithoutGenerate = hasGeneratedSaveData || adcoWasRun ? null : dealToSaveWithoutGenerate({
    original: originalDealRef.current?.data,
    current: selectedRows[0],
    changes: pendingSaveChanges,
  });
  const saveSkipsGenerate = dealWithoutGenerate !== null;
  const saveReady = hasGeneratedSaveData || saveSkipsGenerate;

  // The exact /save request, built when the confirmation opens and again on
  // Confirm, so a state update in between (e.g. the purchase-price fetch
  // resolving) cannot change what gets written without the user seeing it.
  const buildSaveRequest = () => ({
    deal: saveSkipsGenerate ? dealWithoutGenerate : selectedRows[0],
    // A save that skips Generate writes only the deal inputs (CF_INPUTS_RAW). Send no
    // generated rows: selecting another deal leaves the previous deal's cashflow_main behind.
    cashflow: hasGeneratedSaveData ? cashflowMainData : [],
    analytics: hasGeneratedSaveData ? analyticsMainData : [],
    parsed: hasGeneratedSaveData ? parsedData : [],
    // Only persist ADCO data if the user re-ran ADCO during this session.
    // Otherwise the data is just what we fetched from Snowflake on load and
    // re-saving it would create a no-op duplicate row.
    adco: adcoWasRun && adcoData.length > 0 ? adcoData : null,
    tranches: selectedTranches,
    ownPcts: ownPctTrancheTargets,
    purchasePrices: priceTrancheTargets,
  });

  // Save deal with cashflow and analytics data (single transaction)
  const handleSave = () => {
    if (!selectedRows.length) {
      console.warn('No deal selected for saving');
      showNotification('Please select a deal to save', 'warning');
      return;
    }

    if (!selectedTranches.length) {
      console.warn('No tranches selected');
      showNotification('Please select tranches first before generating and saving data.', 'warning');
      return;
    }

    if (!saveReady) {
      console.warn('Must generate cashflow, analytics_main, and parsed data before saving');
      showNotification('Please generate cashflow analytics first. Only Security Owner edits save without a Generate.', 'warning');
      return;
    }

    if (generatedWithYieldOrSpread) {
      console.warn('Cannot save when analytics were generated with a custom yield/spread');
      showNotification('Saving is disabled when a custom yield or spread is used. Clear it (a custom price is fine) and regenerate to save.', 'warning');
      return;
    }

    saveRequestRef.current = buildSaveRequest();
    setSaveChanges(pendingSaveChanges);

    // Open confirmation dialog
    setSaveConfirmOpen(true);
  };

  // Perform the actual save after confirmation
  const handleConfirmSave = async () => {
    setSaveConfirmOpen(false);

    const request = saveRequestRef.current;
    saveRequestRef.current = null;
    // Anything that changed while the dialog was open is not in the list the user
    // confirmed, and may no longer be saveable (e.g. without a Generate).
    if (!request || !saveReady || generatedWithYieldOrSpread || !isSameSaveRequest(request, buildSaveRequest())) {
      showNotification('The deal changed while the confirmation was open. Review the changes and save again.', 'warning');
      return;
    }

    setLoading(true);

    try {
      // Save deal, cashflow_main (as cashflow), analytics_main (as analytics), parsed data, ADCO data, and selected tranches in single transaction
      await saveDeal(
        request.deal,
        request.cashflow,
        request.analytics,
        request.parsed,
        userId,
        request.adco,
        request.tranches,
        request.ownPcts,
        request.purchasePrices
      );

      showNotification('Deal saved successfully!', 'success');

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);

      // Refresh the deals list
      loadDeals();

    } catch (error) {
      if (!notifyPermissionError(showNotification, error)) {
        showNotification(`Save failed: ${error.message || 'Unknown error'}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSave = () => {
    saveRequestRef.current = null;
    setSaveConfirmOpen(false);
  };

  const handleRunAdco = async () => {
    if (!selectedRows.length || !selectedTranches.length) {
      console.warn('No deal or tranches selected for ADCO run');
      return;
    }

    const deal = selectedRows[0]['Deal Name'];
    const assetType = selectedRows[0]['Type'] || '';
    const trancheMap = selectedRows[0].tranche_map || null;
    const md = dayjs(modifiedDate).format('YYYY-MM-DD');
    const runAsofdt = useAsofdt
      ? dayjs(asofdt).format('YYYY-MM-DD')
      : (selectedRows[0].ASOFDT ? dayjs(selectedRows[0].ASOFDT).format('YYYY-MM-DD') : null);

    setAdcoLoading(true);
    try {
      const response = await runDealAdco(
        deal,
        assetType,
        selectedTranches,
        md,
        null,
        null,
        trancheMap,
        runAsofdt
      );
      setAdcoData(response.data || []);
      setAdcoWasRun(true); // User re-ran ADCO; include in next save
      setHasUnsavedChanges(true); // Mark as unsaved since ADCO data changed
    } catch (error) {
      if (!notifyPermissionError(showNotification, error)) {
        const errorMessage = error?.response?.data?.error
          || error?.response?.data?.message
          || error?.message
          || 'Unknown error';
        showNotification(`ADCO run failed: ${errorMessage}`, 'error');
      }
    } finally {
      setAdcoLoading(false);
    }
  };


  // --- RENDER -----------------------------------------------------------

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Professional Financial Trading Interface */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: theme.palette.background.default,
          px: isMobile ? 1.5 : 3,
          py: isMobile ? 2 : 3,
          pb: isMobile ? 14 : 3, // Extra padding bottom on mobile for action bar
        }}
      >
        {/* Compact Header & Controls */}
        <Paper
          elevation={1}
          sx={{
            mb: isMobile ? 1.5 : 2,
            p: isMobile ? 1.5 : 2,
            borderRadius: 1,
            bgcolor: theme.palette.background.paper
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            mb: 2,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: isMobile ? '18px' : '20px',
                  mb: 0.5
                }}
              >
                PAM Trade App
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: isMobile ? '11px' : '12px',
                  display: 'block'
                }}
              >
                {selectedRows.length === 0 ?
                  'Select Deal First' :
                  `Working on: ${selectedRows[0]['Deal Name']} ${selectedTranches.length === 0 ?
                    '→ Select Tranches First' :
                    (saveReady ?
                      '✓ Ready to Save' :
                      '→ Generate Analytics Required')
                  }`
                }
              </Typography>
            </Box>
            {/* Desktop Action Buttons */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setAddSecurityOpen(true)}
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '16px',
                    minHeight: 42,
                    borderRadius: 2
                  }}
                >
                  Add Security
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleClearDeal}
                  disabled={selectedRows.length === 0}
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '16px',
                    minHeight: 42,
                    borderRadius: 2
                  }}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading || !selectedTranches.length || !saveReady || generatedWithYieldOrSpread}
                  sx={{
                    bgcolor: loading ? '#9ca3af' : '#16a34a',
                    color: 'white',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '16px',
                    minHeight: 42,
                    borderRadius: 2,
                    boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)',
                    '&:hover': {
                      bgcolor: '#15803d'
                    },
                    '&:disabled': {
                      bgcolor: '#d1d5db',
                      color: '#9ca3af',
                      boxShadow: 'none'
                    }
                  }}
                >
                  {loading ? 'Saving...' : hasUnsavedChanges ? '⚠ Save Deal' : 'Save Deal'}
                </Button>
                {generatedWithYieldOrSpread && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#b45309', alignSelf: 'center', maxWidth: 240, lineHeight: 1.2 }}
                  >
                    Save disabled: generated with a custom yield/spread. A custom price is fine — clear the yield/spread and regenerate to save.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Filter Bar - Collapsible on Mobile */}
          {isMobile ? (
            <CollapsibleFilterBar
              modifiedDate={modifiedDate}
              onModifiedDateChange={setModifiedDate}
              types={types}
              selTypes={selTypes}
              onSelTypesChange={setSelTypes}
              owners={owners}
              selOwners={selOwners}
              onSelOwnersChange={setSelOwners}
              dealName={dealNameFilter}
              onDealNameChange={setDealNameFilter}
              adcoOnly={adcoOnly}
              onAdcoOnlyChange={setAdcoOnly}
              isDealLoaded={selectedRows.length > 0}
            />
          ) : (
            <FilterBar
              modifiedDate={modifiedDate}
              onModifiedDateChange={setModifiedDate}
              types={types}
              selTypes={selTypes}
              onSelTypesChange={setSelTypes}
              owners={owners}
              selOwners={selOwners}
              onSelOwnersChange={setSelOwners}
              dealName={dealNameFilter}
              onDealNameChange={setDealNameFilter}
              adcoOnly={adcoOnly}
              onAdcoOnlyChange={setAdcoOnly}
            />
          )}

          {/* Tranche Selection & Options Row - Only show when deal selected */}
          {selectedRows.length > 0 && (
            <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center" sx={{ mb: 2, mt: isMobile ? 2.5 : 0 }}>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 500 }}>Select Tranches</InputLabel>
                  <Select
                    multiple
                    value={selectedTranches}
                    onChange={e => setSelectedTranches(e.target.value)}
                    label="Select Tranches"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.slice(0, isMobile ? 3 : 2).map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '11px',
                              bgcolor: '#f3e8ff',
                              color: '#6b21a8',
                              fontWeight: 600
                            }}
                          />
                        ))}
                        {selected.length > (isMobile ? 3 : 2) && (
                          <Chip
                            label={`+${selected.length - (isMobile ? 3 : 2)} more`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '11px',
                              bgcolor: '#f3f4f6',
                              color: '#6b7280'
                            }}
                          />
                        )}
                        {selected.length === 0 && (
                          <Typography sx={{ fontSize: '14px', color: '#9ca3af' }}>
                            No tranches selected
                          </Typography>
                        )}
                      </Box>
                    )}
                    sx={{
                      minHeight: isMobile ? 50 : 48,
                      fontSize: isMobile ? '14px' : '15px',
                      '& .MuiSelect-select': {
                        py: isMobile ? 1.8 : 1.5
                      }
                    }}
                  >
                    {availableTranches.map(t => (
                      <MenuItem key={t} value={t} sx={{ fontSize: isMobile ? '14px' : '15px', minHeight: isMobile ? 48 : 'auto' }}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useAsofdt}
                      onChange={e => setUseAsofdt(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: '#0284c7',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#0284c7',
                          },
                        },
                      }}
                    />
                  }
                  label="Custom ASOFDT"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: 500,
                      color: theme.palette.text.primary
                    }
                  }}
                />
              </Grid>
              {useAsofdt && (
                <Grid item xs={12} sm={2}>
                  <DatePicker
                    label="ASOFDT"
                    value={asofdt}
                    onChange={setAsofdt}
                    renderInput={params =>
                      <TextField
                        {...params}
                        fullWidth
                        sx={{
                          '& .MuiInputBase-root': {
                            minHeight: isMobile ? 50 : 48,
                            fontSize: isMobile ? '14px' : '15px'
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: isMobile ? '14px' : '16px',
                            fontWeight: 500
                          }
                        }}
                      />
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}

          {/* Generate Button - Desktop only */}
          {!isMobile && selectedRows.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => setGenerateDialogAnchorEl(e.currentTarget)}
              disabled={calculateDisabled || selectedTranches.length === 0 || generationLoading}
              sx={{
                mt: 2,
                fontWeight: 600,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontSize: '16px',
                minHeight: 42,
                borderRadius: 2
              }}
            >
              {generationLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'white' }} />
                  Generating...
                </Box>
              ) : selectedTranches.length === 0 ? (
                'Select Tranches First'
              ) : hasGeneratedData ? (
                'Re-Generate'
              ) : (
                'Generate Cashflow & Analytics'
              )}
            </Button>
          )}
        </Paper>

        {/* === Professional Trading Dashboard === */}
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'stretch', minHeight: '70vh' }}>

          {/* Securities Portfolio (Left Panel) - Hidden on mobile when deal is selected */}
          {(!isMobile || selectedRows.length === 0) && (
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Card
                elevation={4}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
                    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden'
                }}
              >
                <Box
                  sx={{
                    px: 3,
                    py: 2.5,
                    bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#1e293b',
                    color: 'white',
                    borderBottom: theme.palette.mode === 'dark' ? '1px solid #334155' : '1px solid #334155'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          mb: 0.5,
                          letterSpacing: '-0.025em'
                        }}
                      >
                        Deals
                      </Typography>
                    </Box>
                    {deals.length > 0 && (
                      <Chip
                        label={`${deals.length} deals`}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontWeight: 500,
                          fontSize: '11px'
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <CardContent sx={{ flex: 1, p: isMobile ? 1.5 : 3, minHeight: 0, bgcolor: theme.palette.background.paper }}>
                  {isMobile ? (
                    <MobileDealCards
                      deals={deals}
                      onEdit={handleEditDeal}
                      onClone={handleCloneDeal}
                      onLoad={loadDeal}
                      loading={loading}
                    />
                  ) : (
                    <DealGrid
                      deals={deals}
                      columns={columns}
                      onSelectionChanged={handleGridSelectionChanged}
                      handleEdit={handleEditDeal}
                      handleClone={handleCloneDeal}
                      handleLoad={loadDeal}
                      loading={loading}
                    />
                  )}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Right Column - ADCO + Analytics + Cashflow - Only show when deal is selected */}
          {selectedRows.length > 0 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3, minWidth: 0, alignSelf: 'flex-start' }}>
              {isMobile ? (
                <MobileTabbedPanels
                  tabValue={mobileTabValue}
                  onTabChange={setMobileTabValue}
                  selectedRows={selectedRows}
                  selectedTranches={selectedTranches}
                  adcoEnabled={adcoEnabled}
                  analyticsData={analyticsData}
                  analyticsLoading={analyticsLoading}
                  panelAnalyticsTranches={panelAnalyticsTranches}
                  onAnalyticsTranchesChange={setPanelAnalyticsTranches}
                  priceTrancheTargets={priceTrancheTargets}
                  onPriceTrancheTargetsChange={setPriceTrancheTargets}
                  yieldTrancheTargets={yieldTrancheTargets}
                  onYieldTrancheTargetsChange={setYieldTrancheTargets}
                  spreadTrancheTargets={spreadTrancheTargets}
                  onSpreadTrancheTargetsChange={setSpreadTrancheTargets}
                  ownPctTrancheTargets={ownPctTrancheTargets}
                  onOwnPctTrancheTargetsChange={setOwnPctTrancheTargets}
                  equityTranches={equityTranches}
                  priceData={priceData}
                  onGenerate={handleGenerateCashflowAnalytics}
                  generationLoading={generationLoading}
                  generateDisabled={calculateDisabled || selectedTranches.length === 0}
                  hasGeneratedData={hasGeneratedData}
                  cashflowData={cashflowData}
                  cashflowMonthlyData={cashflowMonthlyData}
                  cashflowGrain={cashflowGrain}
                  onCashflowGrainChange={setCashflowGrain}
                  cashflowLoading={cashflowLoading}
                  panelCashflowTranche={panelCashflowTranche}
                  onCashflowTrancheChange={setPanelCashflowTranche}
                  panelAdcoTranche={panelAdcoTranche}
                  onAdcoTrancheChange={setPanelAdcoTranche}
                  modifiedDate={modifiedDate}
                  adcoData={effectiveAdcoData}
                  adcoApplied={effectiveAdcoData.length > 0}
                  onRunAdco={handleRunAdco}
                  adcoLoading={adcoLoading}
                  generationError={generationError}
                />
              ) : (
                <DesktopStackedPanels
                  selectedRows={selectedRows}
                  selectedTranches={selectedTranches}
                  adcoEnabled={adcoEnabled}
                  analyticsData={analyticsData}
                  analyticsLoading={analyticsLoading}
                  panelAnalyticsTranches={panelAnalyticsTranches}
                  onAnalyticsTranchesChange={setPanelAnalyticsTranches}
                  priceTrancheTargets={priceTrancheTargets}
                  onPriceTrancheTargetsChange={setPriceTrancheTargets}
                  yieldTrancheTargets={yieldTrancheTargets}
                  onYieldTrancheTargetsChange={setYieldTrancheTargets}
                  spreadTrancheTargets={spreadTrancheTargets}
                  onSpreadTrancheTargetsChange={setSpreadTrancheTargets}
                  ownPctTrancheTargets={ownPctTrancheTargets}
                  onOwnPctTrancheTargetsChange={setOwnPctTrancheTargets}
                  equityTranches={equityTranches}
                  priceData={priceData}
                  shockBps={shockBps}
                  onShockBpsChange={setShockBps}
                  onGenerate={handleGenerateCashflowAnalytics}
                  generationLoading={generationLoading || comparisonLoading}
                  generateDisabled={calculateDisabled || selectedTranches.length === 0}
                  hasGeneratedData={hasGeneratedData}
                  cashflowData={cashflowData}
                  cashflowMonthlyData={cashflowMonthlyData}
                  cashflowGrain={cashflowGrain}
                  onCashflowGrainChange={setCashflowGrain}
                  cashflowLoading={cashflowLoading}
                  panelCashflowTranche={panelCashflowTranche}
                  onCashflowTrancheChange={setPanelCashflowTranche}
                  panelAdcoTranche={panelAdcoTranche}
                  onAdcoTrancheChange={setPanelAdcoTranche}
                  modifiedDate={modifiedDate}
                  adcoData={effectiveAdcoData}
                  adcoApplied={effectiveAdcoData.length > 0}
                  onRunAdco={handleRunAdco}
                  adcoLoading={adcoLoading}
                  generationError={generationError}
                  showComparison={showComparison}
                  comparisonData={comparisonData}
                  comparisonLoading={comparisonLoading}
                  onCloseComparison={() => { setShowComparison(false); setComparisonData(null); }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Modals */}
        <AddSecurityModal
          open={addSecurityOpen}
          modifiedDate={dayjs(modifiedDate).format('YYYY-MM-DD')}
          onClose={() => {
            setAddSecurityOpen(false);
            setEditSecurityData(null);
          }}
          userId={userId}
          editData={editSecurityData}
          fullScreen={isMobile}
          onSuccess={(updatedSecurityData) => {
            const isEditingExisting = !!editSecurityData?.['Deal Name'];
            const dealName = editSecurityData?.['Deal Name'];
            // TEMP edit-debug: did the form return the edits, and does the name match?
            console.log('[edit-debug] onSuccess', {
              isEditingExisting,
              dealName,
              updatedSecurityData,
            });

            const appendTranche = (prevDeals) => {
              const newDealName = updatedSecurityData['Deal Name'];
              const existingIdx = prevDeals.findIndex(d => d['Deal Name'] === newDealName);
              if (existingIdx !== -1) {
                // Deal already exists — append new Tranche/CUSIP to the condensed row
                return prevDeals.map((d, i) => {
                  if (i !== existingIdx) return d;
                  return {
                    ...d,
                    ...mergeAlignedDealTranches(d, updatedSecurityData),
                  };
                });
              }
              // Brand-new deal
              return [updatedSecurityData, ...prevDeals];
            };

            // Update the deals list locally without API call
            setDeals(prevDeals => {
              if (isEditingExisting) {
                return prevDeals.map(deal => {
                  if (deal['Deal Name'] === dealName) {
                    return { ...deal, ...updatedSecurityData };
                  }
                  return deal;
                });
              }
              return appendTranche(prevDeals);
            });
            setAllDeals(prevDeals => {
              if (isEditingExisting) {
                return prevDeals.map(deal => {
                  if (deal['Deal Name'] === dealName) {
                    return { ...deal, ...updatedSecurityData };
                  }
                  return deal;
                });
              }
              return appendTranche(prevDeals);
            });

            // Auto-load the deal after creating or editing so the user can immediately work with it
            if (isEditingExisting) {
              const mergedRow = { ...editSecurityData, ...updatedSecurityData };
              loadDeal(mergedRow);
            } else {
              // Brand-new deal: there is no prior Snowflake state, so the Save
              // diff should report every field as new.
              originalDealRef.current = { name: updatedSecurityData['Deal Name'], data: {} };
              loadDeal(updatedSecurityData);
            }
          }}
        />

        {/* Save Confirmation Dialog */}
        <SaveConfirmDialog
          open={saveConfirmOpen}
          onClose={handleCancelSave}
          onConfirm={handleConfirmSave}
          dealName={selectedRows[0]?.['Deal Name'] || ''}
          changes={saveChanges}
          fullScreen={isMobile}
        />

        <GenerateCustomPricesDialog
          open={Boolean(generateDialogAnchorEl)}
          anchorEl={generateDialogAnchorEl}
          onClose={() => setGenerateDialogAnchorEl(null)}
          selectedTranches={selectedTranches}
          priceTrancheTargets={priceTrancheTargets}
          onPriceTrancheTargetsChange={setPriceTrancheTargets}
          yieldTrancheTargets={yieldTrancheTargets}
          onYieldTrancheTargetsChange={setYieldTrancheTargets}
          spreadTrancheTargets={spreadTrancheTargets}
          onSpreadTrancheTargetsChange={setSpreadTrancheTargets}
          ownPctTrancheTargets={ownPctTrancheTargets}
          onOwnPctTrancheTargetsChange={setOwnPctTrancheTargets}
          equityTranches={equityTranches}
          onGenerate={() => { setGenerateDialogAnchorEl(null); handleGenerateCashflowAnalytics(); }}
          generationLoading={generationLoading}
        />

        {/* Mobile Bottom Action Bar */}
        {isMobile && (
          <MobileBottomActionBar
            selectedRows={selectedRows}
            selectedTranches={selectedTranches}
            cashflowData={cashflowData}
            analyticsMainData={analyticsMainData}
            parsedData={parsedData}
            generationLoading={generationLoading}
            loading={loading}
            hasUnsavedChanges={hasUnsavedChanges}
            generatedWithYieldOrSpread={generatedWithYieldOrSpread}
            saveSkipsGenerate={saveSkipsGenerate}
            onGenerate={handleGenerateCashflowAnalytics}
            onAddSecurity={() => setAddSecurityOpen(true)}
            onEditSecurity={() => handleEditDeal(selectedRows[0])}
            onClear={handleClearDeal}
            onSave={handleSave}
          />
        )}

        {/* Loading Overlay */}
        {loading && (
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              bgcolor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1500,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}
